import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-32 pb-24 px-6 flex justify-center">
        <div className="w-full max-w-md">
          <h1 className="text-4xl font-bold tracking-tighter" data-testid="login-title">
            Welcome back
          </h1>
          <p className="text-[#a0a0a0] mt-3">Log in to manage your orders and downloads.</p>

          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 text-white focus:border-white outline-none"
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="text-xs text-[#a0a0a0] uppercase tracking-widest">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 text-white focus:border-white outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full squircle bg-white text-black py-3.5 font-medium hover:bg-[#f2f2f2] btn-anim inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Log in"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="my-8 flex items-center gap-3 text-xs text-[#666]">
            <span className="h-px flex-1 bg-[#1e1e1e]" /> OR <span className="h-px flex-1 bg-[#1e1e1e]" />
          </div>

          <button
            data-testid="login-google"
            onClick={googleLogin}
            className="w-full squircle border border-[#2b2b2b] py-3.5 font-medium hover:bg-[#111] btn-anim"
          >
            Continue with Google
          </button>

          <div className="mt-8 text-sm text-[#a0a0a0]">
            No account?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-white underline">
              Create one
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
